import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';

function TransactionMatcher({ csvData, players: initialPlayers, onSave }) {
    const [matches, setMatches] = useState({});
    const [matchConfidence, setMatchConfidence] = useState({}); // Store confidence levels
    const [players, setPlayers] = useState(initialPlayers || []);
    const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
    const [playerError, setPlayerError] = useState(null);

    // Fetch all players from the database to ensure we have complete data
    useEffect(() => {
        const fetchAllPlayers = async () => {
            setIsLoadingPlayers(true);
            setPlayerError(null);
            try {
                const teamId = sessionStorage.getItem('selected_team_id');
                if (!teamId) {
                    throw new Error('No team ID found in session storage');
                }
                
                const response = await api.get(`${process.env.REACT_APP_API_URL}/get_players.php?team_id=${teamId}`);
                
                if (response.data.success) {
                    // Merge with any players we already have to ensure we have complete data
                    const newPlayers = response.data.players;
                    
                    // Create a map of player IDs we already have for quick lookup
                    const existingPlayerMap = new Map(
                        initialPlayers.map(p => [p.player_id, p])
                    );
                    
                    // Add any players we don't already have
                    const mergedPlayers = [...initialPlayers];
                    for (const player of newPlayers) {
                        if (!existingPlayerMap.has(player.player_id)) {
                            mergedPlayers.push(player);
                        }
                    }
                    
                    setPlayers(mergedPlayers);
                    console.log('Fetched all players:', mergedPlayers.length);
                } else {
                    setPlayerError('Failed to fetch players: ' + (response.data.message || 'Unknown error'));
                }
            } catch (error) {
                console.error('Error fetching players:', error);
                setPlayerError('Error loading players: ' + error.message);
            } finally {
                setIsLoadingPlayers(false);
            }
        };
        
        fetchAllPlayers();
    }, [initialPlayers]);

    // Function to calculate similarity between two strings
    const calculateSimilarity = (str1, str2) => {
        str1 = str1.toLowerCase();
        str2 = str2.toLowerCase();
        
        // Check for exact match or contains
        if (str1 === str2) return 100;
        if (str1.includes(str2) || str2.includes(str1)) return 90;
        
        // Calculate Levenshtein distance
        const len1 = str1.length;
        const len2 = str2.length;
        const matrix = Array(len1 + 1).fill().map(() => Array(len2 + 1).fill(0));
        
        for (let i = 0; i <= len1; i++) matrix[i][0] = i;
        for (let j = 0; j <= len2; j++) matrix[0][j] = j;
        
        for (let i = 1; i <= len1; i++) {
            for (let j = 1; j <= len2; j++) {
                const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
                matrix[i][j] = Math.min(
                    matrix[i - 1][j] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j - 1] + cost
                );
            }
        }
        
        // Convert distance to similarity percentage
        const maxLength = Math.max(len1, len2);
        const similarity = ((maxLength - matrix[len1][len2]) / maxLength) * 100;
        return similarity;
    };

    // Auto-match players on component mount
    useEffect(() => {
        const autoMatches = {};
        const confidenceScores = {};
        
        csvData.forEach((row, index) => {
            let bestMatch = null;
            let highestSimilarity = 0;
            
            players.forEach(player => {
                const similarity = calculateSimilarity(
                    row.description,
                    player.name_reference || `${player.first_name} ${player.last_name}`
                );
                
                if (similarity > highestSimilarity) {
                    highestSimilarity = similarity;
                    bestMatch = player.player_id;
                }
            });
            
            confidenceScores[index] = highestSimilarity;
            if (highestSimilarity > 80) {
                autoMatches[index] = bestMatch;
            }
        });
        
        setMatchConfidence(confidenceScores);
        setMatches(autoMatches);
    }, [csvData, players]);

    const handlePlayerSelect = (rowIndex, playerId) => {
        // If empty or "Select Player..." is chosen, remove the match
        if (!playerId) {
            const newMatches = { ...matches };
            delete newMatches[rowIndex];
            setMatches(newMatches);
            return;
        }

        setMatches(prev => ({
            ...prev,
            [rowIndex]: playerId
        }));
    };

    const handleSave = () => {
        // Only process rows that have a valid player selected
        const validMatches = Object.entries(matches)
            .filter(([_, playerId]) => playerId && playerId !== '')
            .reduce((acc, [index, playerId]) => {
                acc[index] = playerId;
                return acc;
            }, {});

        const matchedTransactions = csvData
            .map((row, index) => ({
                ...row,
                player_id: validMatches[index] || null
            }))
            .filter(row => row.player_id !== null);

        if (matchedTransactions.length === 0) {
            alert('Please select at least one valid player match before saving.');
            return;
        }

        onSave(matchedTransactions);
    };

    const getMatchIndicator = (index) => {
        // Only show success indicator if there's a valid player selected
        if (matches[index] && matches[index] !== '') {
            return (
                <i 
                    className="fas fa-check-circle success" 
                    title={`${matchConfidence[index].toFixed(0)}% match`} 
                />
            );
        }

        // Show warning or error based on confidence score
        if (matchConfidence[index] > 60) {
            return (
                <i 
                    className="fas fa-question-circle warning" 
                    title={`${matchConfidence[index].toFixed(0)}% match`} 
                />
            );
        }

        return (
            <i 
                className="fas fa-times-circle error" 
                title="No good match found" 
            />
        );
    };

    return (
        <div className="transaction-matcher">
            {isLoadingPlayers && (
                <div className="loading-message">
                    <i className="fas fa-spinner fa-spin"></i> Loading all players from database...
                </div>
            )}
            
            {playerError && (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i> {playerError}
                </div>
            )}
            
            <div className="transactions-grid">
                <div className="grid-header">
                    <span>Date</span>
                    <span>Description</span>
                    <span>Amount</span>
                    <span>Assign To Player</span>
                    <span>Match</span>
                </div>
                {csvData.map((row, index) => (
                    <div key={index} className="transaction-row">
                        <span>{row.date}</span>
                        <span>{row.description}</span>
                        <span>£{Number(row.amount).toFixed(2)}</span>
                        <span>
                            <select
                                value={matches[index] || ''}
                                onChange={(e) => handlePlayerSelect(index, e.target.value)}
                                className={matches[index] && matches[index] !== '' ? 'matched' : ''}
                                disabled={isLoadingPlayers}
                            >
                                <option value="">Select Player...</option>
                                {players.map(player => (
                                    <option key={player.player_id} value={player.player_id}>
                                        {player.first_name} {player.last_name}
                                    </option>
                                ))}
                            </select>
                        </span>
                        <span className="match-indicator">
                            {getMatchIndicator(index)}
                        </span>
                    </div>
                ))}
            </div>

            <div className="actions">
                <button 
                    onClick={handleSave}
                    disabled={Object.values(matches).filter(Boolean).length === 0 || isLoadingPlayers}
                >
                    Save Matched Transactions
                </button>
            </div>
        </div>
    );
}

export default TransactionMatcher; 